import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/EngagementCampaignReportSummary.graphql';
import { Query, QueryengagementCampaignReportSummaryArgs } from '../../__generated__/globalTypes';

export const useEngagementCampaignReportSummaryQuery = (
  options?: QueryHookOptions<Query, QueryengagementCampaignReportSummaryArgs>
) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryengagementCampaignReportSummaryArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useEngagementCampaignReportSummaryLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryengagementCampaignReportSummaryArgs>
): QueryResult<Query, QueryengagementCampaignReportSummaryArgs> & {
  getEngagementCampaignReportSummary: LazyQueryExecFunction<Query, QueryengagementCampaignReportSummaryArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getEngagementCampaignReportSummary, result] = useLazyQuery<Query, QueryengagementCampaignReportSummaryArgs>(
    QUERY,
    {
      onError: (error) => {
        enqueueSnackbar(t(error.message), { variant: 'error' });
      },
      ...options
    }
  );

  return { getEngagementCampaignReportSummary, ...result };
};

export const useEngagementCampaignReportSummaryPromiseQuery = () => {
  const client = useApolloClient();

  const getEngagementCampaignReportSummary = useCallback(
    (options?: Omit<QueryOptions<QueryengagementCampaignReportSummaryArgs, Query>, 'query'>) =>
      client.query<Query, QueryengagementCampaignReportSummaryArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getEngagementCampaignReportSummary };
};
// Suspense Query Hooks
export const useEngagementCampaignReportSummarySuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryengagementCampaignReportSummaryArgs>
) => {
  const result = useSuspenseQuery<Query, QueryengagementCampaignReportSummaryArgs>(QUERY, {
    ...options
  });

  return result;
};
